/**
 * Projects and graph links in a scope's repository (git sync Stage 2, S2.6).
 * Pure — scopeProjects.js does the reading.
 *
 *   projects/<name>-<id8>.yaml   a project: its fields and every link that
 *                                touches it (membership "contains" and the
 *                                drawn ones: relates_to / covers / depends_on / documents)
 *   projects/links.yaml          the drawn links between two other items
 *
 * A project file also lists the code repositories it links (card 5c626304):
 * name, remote, branch and note. A repository known only by a local folder
 * stays on this device: its path means nothing on another machine.
 *
 * Links are by node id ("document:<id>", "task:<id>", "request:<id>" …). A
 * link to something that is not in this repository (a request in an API
 * workspace, say) is kept by id with a note — never dropped (plan: "Projects").
 */
import yaml from 'js-yaml';
import { slugOf, id8 } from './scopeDocumentsLayout';

export const LINKS_FILE = 'projects/links.yaml';
export const projectPath = (project) => `projects/${slugOf(project.name, 'project')}-${id8(project.id)}.yaml`;

/** Node types whose items live in a scope repository; anything else is "not in this repository". */
const IN_REPO = new Set(['document', 'task', 'board', 'project', 'folder', 'git_repo']);
const typeOf = (nodeId) => String(nodeId || '').split(':')[0];
const NODE_ID = /^[a-z_]+:[A-Za-z0-9_.:-]{1,128}$/;
export const isNodeId = (v) => NODE_ID.test(String(v || ''));

const byKey = (a, b) => `${a.rel}|${a.from}|${a.to}`.localeCompare(`${b.rel}|${b.from}|${b.to}`);

function linkLines(links) {
  const out = [];
  for (const l of [...links].sort(byKey)) {
    const note = [l.from, l.to].some((n) => !IN_REPO.has(typeOf(n))) ? '  # not in this repository' : '';
    out.push(`  - { rel: ${JSON.stringify(l.rel)}, from: ${JSON.stringify(l.from)}, to: ${JSON.stringify(l.to)} }${note}`);
  }
  return out.length ? out : ['  []'];
}

function repoLines(repos) {
  const out = [];
  for (const r of [...repos].filter((x) => x.remote).sort((a, b) => String(a.id).localeCompare(String(b.id)))) {
    const fields = { id: String(r.id), name: r.name || '', remote: r.remote };
    if (r.branch) fields.branch = r.branch;
    if (r.description) fields.description = r.description;
    out.push(`  - { ${Object.entries(fields).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(', ')} }`);
  }
  return out;
}

export function projectYaml(project, links, repos = []) {
  const repoList = repoLines(repos);
  return [
    'apiboo: project',
    `id: ${JSON.stringify(String(project.id))}`,
    `name: ${JSON.stringify(project.name || '')}`,
    `description: ${JSON.stringify(project.description || '')}`,
    `color: ${JSON.stringify(project.color ?? null)}`,
    `created: ${JSON.stringify(project.created_at ?? null)}`,
    'links:',
    ...linkLines(links),
    ...(repoList.length ? ['repositories:', ...repoList] : []),
    '',
  ].join('\n').replace('links:\n  []', 'links: []');
}

export function linksYaml(links) {
  return ['apiboo: links', 'links:', ...linkLines(links), ''].join('\n').replace('links:\n  []', 'links: []');
}

const str = (v) => (v == null ? '' : String(v));

/** A project / links file → plain data; anything odd is dropped (a file from the repository is data). */
export function readProjectsFile(text) {
  let d;
  try {
    d = yaml.load(String(text || ''), { schema: yaml.JSON_SCHEMA });
  } catch {
    return null;
  }
  // Not a project / links file (empty, a list, no "apiboo:", links that are not a list): unreadable — never "nothing in it".
  if (!d || typeof d !== 'object' || Array.isArray(d) || (d.apiboo !== 'project' && d.apiboo !== 'links')) return null;
  if (d.links != null && !Array.isArray(d.links)) return null;
  if (d.apiboo === 'links' && !Array.isArray(d.links)) return null;
  const links = (Array.isArray(d.links) ? d.links : [])
    .filter((l) => l && typeof l === 'object' && isNodeId(l.from) && isNodeId(l.to) && typeof l.rel === 'string')
    .map((l) => ({ rel: l.rel, from: l.from, to: l.to }));
  const repositories = (Array.isArray(d.repositories) ? d.repositories : [])
    .filter((r) => r && typeof r === 'object' && typeof r.id === 'string' && typeof r.remote === 'string')
    .map((r) => ({ id: r.id, name: str(r.name), remote: r.remote, branch: str(r.branch), description: str(r.description) }));
  return {
    kind: d.apiboo === 'links' ? 'links' : 'project',
    repositories,
    id: str(d.id),
    name: str(d.name),
    description: str(d.description),
    color: d.color == null ? null : d.color,
    created: d.created == null ? null : str(d.created),
    links,
  };
}
